import dagre from '@dagrejs/dagre';
import type { AutoLayoutOptions } from './autoLayout';

export interface NestedLayoutNode {
  id: string;
  width: number;
  height: number;
  parentId?: string | null;
}

export interface NestedLayoutEdge {
  from: string;
  to: string;
}

/** Must match the padding a group-mode container node draws around its members on the
 * canvas, so the layout reserves enough room for it. */
const GROUP_PADDING = 28;
const GROUP_HEADER_HEIGHT = 32;

function newGraph(direction: 'LR' | 'TB', nodesep: number, ranksep: number, marginx: number, marginy: number) {
  const graph = new dagre.graphlib.Graph();
  graph.setGraph({ rankdir: direction, nodesep, ranksep, marginx, marginy });
  graph.setDefaultEdgeLabel(() => ({}));
  return graph;
}

/**
 * One-level-nesting counterpart to computeAutoLayout, generalized over plain nodes that
 * carry their own optional `parentId` (instead of a separate Table/Group split): any node
 * referenced as another node's parentId is treated as a container, sized to fit its
 * members plus padding/header, exactly like computeAutoLayout's group meta-nodes.
 */
export function computeNestedAutoLayout(
  nodes: NestedLayoutNode[],
  edges: NestedLayoutEdge[],
  options: AutoLayoutOptions = {},
): Record<string, { x: number; y: number }> {
  const direction = options.direction ?? 'LR';
  const nodesep = options.nodeSeparation ?? 60;
  const ranksep = options.rankSeparation ?? 140;

  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const membersByParent = new Map<string, NestedLayoutNode[]>();
  for (const node of nodes) {
    if (!node.parentId) continue;
    if (!nodeById.has(node.parentId)) continue;
    const list = membersByParent.get(node.parentId) ?? [];
    list.push(node);
    membersByParent.set(node.parentId, list);
  }

  // Pass 1: compact relative sub-layout per parent container.
  const subLayouts = new Map<string, { positions: Record<string, { x: number; y: number }>; width: number; height: number }>();
  for (const [parentId, members] of membersByParent) {
    const memberIds = new Set(members.map((n) => n.id));
    const subGraph = newGraph(direction, nodesep, ranksep, 0, 0);
    for (const node of members) {
      subGraph.setNode(node.id, { width: node.width, height: node.height });
    }
    for (const edge of edges) {
      if (edge.from === edge.to) continue;
      if (memberIds.has(edge.from) && memberIds.has(edge.to)) {
        subGraph.setEdge(edge.from, edge.to);
      }
    }
    dagre.layout(subGraph);

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const raw: Record<string, { x: number; y: number }> = {};
    for (const node of members) {
      const laidOut = subGraph.node(node.id);
      const left = laidOut.x - laidOut.width / 2;
      const top = laidOut.y - laidOut.height / 2;
      raw[node.id] = { x: left, y: top };
      minX = Math.min(minX, left);
      minY = Math.min(minY, top);
      maxX = Math.max(maxX, left + laidOut.width);
      maxY = Math.max(maxY, top + laidOut.height);
    }
    const positions: Record<string, { x: number; y: number }> = {};
    for (const node of members) {
      positions[node.id] = { x: raw[node.id].x - minX, y: raw[node.id].y - minY };
    }
    subLayouts.set(parentId, { positions, width: maxX - minX, height: maxY - minY });
  }

  // Pass 2: top-level layout of {container meta-nodes, sized incl. padding} + every
  // other node not itself a laid-out member.
  const topGraph = newGraph(direction, nodesep, ranksep, 40, 40);
  const metaIdFor = (id: string): string => {
    if (subLayouts.has(id)) return `meta:${id}`;
    const node = nodeById.get(id);
    if (node?.parentId && subLayouts.has(node.parentId)) return `meta:${node.parentId}`;
    return id;
  };

  for (const [parentId, layout] of subLayouts) {
    topGraph.setNode(`meta:${parentId}`, {
      width: layout.width + GROUP_PADDING * 2,
      height: layout.height + GROUP_PADDING * 2 + GROUP_HEADER_HEIGHT,
    });
  }
  for (const node of nodes) {
    if (node.parentId && subLayouts.has(node.parentId)) continue;
    topGraph.setNode(node.id, { width: node.width, height: node.height });
  }

  const seenEdges = new Set<string>();
  for (const edge of edges) {
    const from = metaIdFor(edge.from);
    const to = metaIdFor(edge.to);
    if (from === to) continue;
    const key = `${from}->${to}`;
    if (seenEdges.has(key)) continue;
    seenEdges.add(key);
    topGraph.setEdge(from, to);
  }

  dagre.layout(topGraph);

  const positions: Record<string, { x: number; y: number }> = {};
  for (const [parentId, layout] of subLayouts) {
    const meta = topGraph.node(`meta:${parentId}`);
    if (!meta) continue;
    const originX = meta.x - meta.width / 2 + GROUP_PADDING;
    const originY = meta.y - meta.height / 2 + GROUP_PADDING + GROUP_HEADER_HEIGHT;
    positions[parentId] = { x: meta.x - meta.width / 2, y: meta.y - meta.height / 2 };
    for (const [nodeId, rel] of Object.entries(layout.positions)) {
      positions[nodeId] = { x: originX + rel.x, y: originY + rel.y };
    }
  }
  for (const node of nodes) {
    if (node.parentId && subLayouts.has(node.parentId)) continue;
    if (subLayouts.has(node.id)) continue; // already positioned above as a container origin
    const laidOut = topGraph.node(node.id);
    if (!laidOut) continue;
    positions[node.id] = { x: laidOut.x - laidOut.width / 2, y: laidOut.y - laidOut.height / 2 };
  }
  return positions;
}
